import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Plane, Car, Users, Package, Clock, MapPin, Phone, Mail, 
  Search, LogOut, RefreshCw, XCircle, Edit, Eye, Plus, Trash2,
  TrendingUp, CheckCircle, AlertCircle, UserCheck, Link2, Bell, Send, DollarSign, Moon, Sun
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HTTP_UNAUTHORIZED = 401;

// Theme Hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("travelcab_theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => {
    localStorage.setItem("travelcab_theme", isDark ? "dark" : "light");
    document.documentElement.classList.toggle("light-mode", !isDark);
  }, [isDark]);

  return { isDark, toggleTheme: () => setIsDark(!isDark) };
};

// Auth Hook — sessionStorage for sensitive tokens (XSS-safe vs localStorage)
const useAuth = () => {
  const [token, setToken] = useState(sessionStorage.getItem("travelcab_token"));
  const [username, setUsername] = useState(sessionStorage.getItem("travelcab_username"));

  const login = (newToken, newUsername) => {
    sessionStorage.setItem("travelcab_token", newToken);
    sessionStorage.setItem("travelcab_username", newUsername);
    setToken(newToken);
    setUsername(newUsername);
  };

  const logout = () => {
    sessionStorage.removeItem("travelcab_token");
    sessionStorage.removeItem("travelcab_username");
    setToken(null);
    setUsername(null);
  };

  return { token, username, login, logout, isAuthenticated: !!token };
};

// API Client
const createApiClient = (token) => {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

// Theme-aware classes
const useThemeClasses = (isDark) => ({
  bg: isDark ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-gray-50 via-emerald-50 to-teal-50",
  card: isDark ? "bg-slate-800/50 border-slate-700" : "bg-white/80 border-emerald-200",
  cardInner: isDark ? "bg-slate-700/30 border-slate-600" : "bg-emerald-50/50 border-emerald-200",
  input: isDark ? "bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" : "bg-white border-emerald-300 text-slate-800 placeholder:text-slate-400",
  inputAlt: isDark ? "bg-slate-600 border-slate-500" : "bg-white border-emerald-300",
  text: isDark ? "text-white" : "text-slate-800",
  textMuted: isDark ? "text-slate-400" : "text-slate-600",
  textAccent: isDark ? "text-emerald-400" : "text-emerald-600",
  header: isDark ? "bg-slate-800/50 border-slate-700" : "bg-white/80 border-emerald-200",
  tableHeader: isDark ? "border-slate-700" : "border-emerald-200",
  tableRow: isDark ? "border-slate-700 hover:bg-slate-700/30" : "border-emerald-100 hover:bg-emerald-50",
  button: "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white",
  buttonOutline: isDark ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
  select: isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-emerald-300 text-slate-800",
  selectContent: isDark ? "bg-slate-700 border-slate-600" : "bg-white border-emerald-200",
  badge: {
    pending: isDark ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-amber-100 text-amber-700 border-amber-300",
    cancelled: isDark ? "bg-rose-500/20 text-rose-400 border-rose-500/30" : "bg-rose-100 text-rose-700 border-rose-300",
    accepted: isDark ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-emerald-100 text-emerald-700 border-emerald-300",
    active: isDark ? "bg-teal-500/20 text-teal-400 border-teal-500/30" : "bg-teal-100 text-teal-700 border-teal-300",
  },
  statCards: {
    primary: isDark ? "from-emerald-500/20 to-teal-500/20 border-emerald-500/30" : "from-emerald-100 to-teal-100 border-emerald-300",
    green: isDark ? "from-emerald-500/20 to-green-500/20 border-emerald-500/30" : "from-emerald-100 to-green-100 border-emerald-300",
    red: isDark ? "from-rose-500/20 to-red-500/20 border-rose-500/30" : "from-rose-100 to-red-100 border-rose-300",
    blue: isDark ? "from-cyan-500/20 to-blue-500/20 border-cyan-500/30" : "from-cyan-100 to-blue-100 border-cyan-300",
    purple: isDark ? "from-violet-500/20 to-purple-500/20 border-violet-500/30" : "from-violet-100 to-purple-100 border-violet-300",
  }
});

// Login Page
const LoginPage = ({ onLogin, isDark, toggleTheme }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const tc = useThemeClasses(isDark);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      onLogin(response.data.token, response.data.username);
      toast.success("Connexion réussie");
    } catch {
      toast.error("Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${tc.bg}`}>
      <div className="absolute top-4 right-4">
        <Button variant="ghost" onClick={toggleTheme} className={tc.textMuted}>
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>
      <Card className={`w-full max-w-md ${tc.card} backdrop-blur-sm`} data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Car className="w-8 h-8 text-white" />
          </div>
          <CardTitle className={`text-2xl font-bold ${tc.text} tracking-tight`}>Travel CAB</CardTitle>
          <CardDescription className={tc.textMuted}>Backoffice de gestion des transferts</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className={tc.textMuted}>Identifiant</Label>
              <Input id="username" data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className={tc.input} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className={tc.textMuted}>Mot de passe</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={tc.input} />
            </div>
            <Button type="submit" className={`w-full ${tc.button} font-semibold`} disabled={loading} data-testid="login-submit">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Stats Card
const StatCard = ({ title, value, icon: Icon, colorClass, isDark }) => {
  const tc = useThemeClasses(isDark);
  return (
    <Card className={`bg-gradient-to-br ${colorClass} border backdrop-blur-sm`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-xs ${tc.textMuted} mb-1`}>{title}</p>
            <p className={`text-2xl font-bold ${tc.text}`}>{value}</p>
          </div>
          <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-white/10' : 'bg-white/60'} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${tc.textAccent}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Status Badge
const StatusBadge = ({ status, internalStatus, isDark }) => {
  const tc = useThemeClasses(isDark);
  if (internalStatus === "pending") {
    return <Badge className={tc.badge.pending}>En attente</Badge>;
  }
  if (internalStatus === "rejected" || status === "cancelled") {
    return <Badge className={tc.badge.cancelled}>Annulé</Badge>;
  }
  if (internalStatus === "accepted") {
    return <Badge className={tc.badge.accepted}>Accepté</Badge>;
  }
  return <Badge className={tc.badge.active}>Actif</Badge>;
};

// Driver/Vehicle Select with linked selection
const LinkedSelect = ({ drivers, vehicles, selectedDriverId, selectedVehicleId, onDriverChange, onVehicleChange, isDark }) => {
  const tc = useThemeClasses(isDark);
  
  const handleDriverSelect = (driverId) => {
    onDriverChange(driverId);
    const driver = drivers.find(d => d.id === driverId);
    if (driver?.default_vehicle_id) {
      onVehicleChange(driver.default_vehicle_id);
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    onVehicleChange(vehicleId);
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle?.default_driver_id) {
      onDriverChange(vehicle.default_driver_id);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label className={tc.textMuted}>Chauffeur</Label>
        <Select value={selectedDriverId || ""} onValueChange={handleDriverSelect}>
          <SelectTrigger className={tc.select}>
            <SelectValue placeholder="Sélectionner un chauffeur" />
          </SelectTrigger>
          <SelectContent className={tc.selectContent}>
            {drivers.map(driver => (
              <SelectItem key={driver.id} value={driver.id} className={`${tc.text} hover:bg-emerald-500/20`}>
                <div className="flex items-center gap-2">
                  <span>{driver.first_name} {driver.last_name}</span>
                  {driver.default_vehicle_id && <Link2 className="w-3 h-3 text-emerald-500" />}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className={tc.textMuted}>Véhicule</Label>
        <Select value={selectedVehicleId || ""} onValueChange={handleVehicleSelect}>
          <SelectTrigger className={tc.select}>
            <SelectValue placeholder="Sélectionner un véhicule" />
          </SelectTrigger>
          <SelectContent className={tc.selectContent}>
            {vehicles.map(vehicle => (
              <SelectItem key={vehicle.id} value={vehicle.id} className={`${tc.text} hover:bg-emerald-500/20`}>
                <div className="flex items-center gap-2">
                  <span>{vehicle.car_model} - {vehicle.plate_number}</span>
                  {vehicle.default_driver_id && <Link2 className="w-3 h-3 text-emerald-500" />}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// Accept/Reject Order Modal
const AcceptRejectModal = ({ order, open, onClose, drivers, vehicles, onAccept, onReject, onCreateDriver, onCreateVehicle, isDark }) => {
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [amountType, setAmountType] = useState("fixed");
  const [driverAmount, setDriverAmount] = useState("");
  const [driverPercentage, setDriverPercentage] = useState("70");
  const [showNewDriver, setShowNewDriver] = useState(false);
  const [showNewVehicle, setShowNewVehicle] = useState(false);
  const [newDriver, setNewDriver] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [newVehicle, setNewVehicle] = useState({ car_model: "", plate_number: "", color: "", seats: 4 });
  const tc = useThemeClasses(isDark);

  const calculatedAmount = useMemo(() => {
    if (!order?.price?.amount) return 0;
    if (amountType === "fixed") return parseFloat(driverAmount) || 0;
    return (order.price.amount * (parseFloat(driverPercentage) || 0) / 100).toFixed(2);
  }, [order, amountType, driverAmount, driverPercentage]);

  if (!order) return null;

  const handleAccept = () => {
    if (!selectedDriverId || !selectedVehicleId) {
      toast.error("Veuillez sélectionner un chauffeur et un véhicule");
      return;
    }
    const acceptData = { driver_id: selectedDriverId, vehicle_id: selectedVehicleId };
    if (amountType === "fixed" && driverAmount) {
      acceptData.driver_amount = parseFloat(driverAmount);
    } else if (amountType === "percentage" && driverPercentage) {
      acceptData.driver_percentage = parseFloat(driverPercentage);
    }
    onAccept(order.order_id, acceptData);
    onClose();
  };

  const handleCreateDriver = async () => {
    const created = await onCreateDriver(newDriver);
    if (created) {
      setSelectedDriverId(created.id);
      setShowNewDriver(false);
      setNewDriver({ first_name: "", last_name: "", phone: "", email: "" });
    }
  };

  const handleCreateVehicle = async () => {
    const created = await onCreateVehicle(newVehicle);
    if (created) {
      setSelectedVehicleId(created.id);
      setShowNewVehicle(false);
      setNewVehicle({ car_model: "", plate_number: "", color: "", seats: 4 });
    }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString("fr-FR") : "-";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl ${tc.card} ${tc.text}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-emerald-500" />
            Nouvelle course #{order.order_id}
          </DialogTitle>
          <DialogDescription className={tc.textMuted}>Acceptez ou refusez cette course</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-4 pr-2">
            <Card className={tc.cardInner}>
              <CardContent className="p-4 grid grid-cols-2 gap-4 text-sm">
                <div><p className={tc.textMuted}>Passager</p><p className={`${tc.text} font-medium`}>{order.main_passenger?.first_name} {order.main_passenger?.last_name}</p></div>
                <div><p className={tc.textMuted}>Date/Heure</p><p className={tc.text}>{formatDate(order.start_time)}</p></div>
                <div><p className={tc.textMuted}>Trajet</p><p className={tc.text}>{order.start_point?.iata || order.start_point?.address || "GPS"} → {order.end_point?.iata || order.end_point?.address || "GPS"}</p></div>
                <div><p className={tc.textMuted}>Prix total</p><p className={`${tc.text} font-bold text-lg`}>{order.price?.amount} {order.price?.currency}</p></div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className={`${tc.text} font-medium`}>Assigner chauffeur & véhicule</h4>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowNewDriver(true)} className={tc.buttonOutline}><Plus className="w-4 h-4 mr-1" /> Chauffeur</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowNewVehicle(true)} className={tc.buttonOutline}><Plus className="w-4 h-4 mr-1" /> Véhicule</Button>
                </div>
              </div>
              <LinkedSelect drivers={drivers} vehicles={vehicles} selectedDriverId={selectedDriverId} selectedVehicleId={selectedVehicleId} onDriverChange={setSelectedDriverId} onVehicleChange={setSelectedVehicleId} isDark={isDark} />
            </div>

            <Card className={tc.cardInner}>
              <CardContent className="p-4 space-y-3">
                <h4 className={`${tc.textAccent} font-medium flex items-center gap-2`}><DollarSign className="w-4 h-4" /> Montant chauffeur</h4>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="amountType" checked={amountType === "fixed"} onChange={() => setAmountType("fixed")} className="accent-emerald-500" />
                    <span className={tc.textMuted}>Montant fixe</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="amountType" checked={amountType === "percentage"} onChange={() => setAmountType("percentage")} className="accent-emerald-500" />
                    <span className={tc.textMuted}>Pourcentage</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {amountType === "fixed" ? (
                    <div className="space-y-1">
                      <Label className={`${tc.textMuted} text-xs`}>Montant (€)</Label>
                      <Input type="number" step="0.01" placeholder="Ex: 35.00" value={driverAmount} onChange={(e) => setDriverAmount(e.target.value)} className={tc.inputAlt} />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Label className={`${tc.textMuted} text-xs`}>Pourcentage (%)</Label>
                      <Input type="number" min="0" max="100" placeholder="Ex: 70" value={driverPercentage} onChange={(e) => setDriverPercentage(e.target.value)} className={tc.inputAlt} />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className={`${tc.textMuted} text-xs`}>Montant calculé</Label>
                    <div className={`${isDark ? 'bg-slate-600/50 border-slate-500' : 'bg-emerald-50 border-emerald-300'} border rounded-md px-3 py-2 text-lg font-bold text-emerald-500`}>
                      {calculatedAmount} {order.price?.currency}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {showNewDriver && (
              <Card className={tc.cardInner}>
                <CardContent className="p-4 space-y-3">
                  <h5 className={`${tc.textAccent} font-medium`}>Nouveau chauffeur</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Prénom *" value={newDriver.first_name} onChange={(e) => setNewDriver({...newDriver, first_name: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Nom *" value={newDriver.last_name} onChange={(e) => setNewDriver({...newDriver, last_name: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Téléphone *" value={newDriver.phone} onChange={(e) => setNewDriver({...newDriver, phone: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Email" value={newDriver.email} onChange={(e) => setNewDriver({...newDriver, email: e.target.value})} className={tc.inputAlt} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateDriver} className={tc.button}>Créer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowNewDriver(false)}>Annuler</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {showNewVehicle && (
              <Card className={tc.cardInner}>
                <CardContent className="p-4 space-y-3">
                  <h5 className={`${tc.textAccent} font-medium`}>Nouveau véhicule</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Modèle *" value={newVehicle.car_model} onChange={(e) => setNewVehicle({...newVehicle, car_model: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Plaque *" value={newVehicle.plate_number} onChange={(e) => setNewVehicle({...newVehicle, plate_number: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Couleur" value={newVehicle.color} onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})} className={tc.inputAlt} />
                    <Input placeholder="Places" type="number" value={newVehicle.seats} onChange={(e) => setNewVehicle({...newVehicle, seats: parseInt(e.target.value)})} className={tc.inputAlt} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateVehicle} className={tc.button}>Créer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowNewVehicle(false)}>Annuler</Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className={`flex justify-between gap-3 border-t ${isDark ? 'border-slate-700' : 'border-emerald-200'} pt-4`}>
          <Button variant="destructive" onClick={() => { onReject(order.order_id); onClose(); }} data-testid="reject-order-btn">
            <XCircle className="w-4 h-4 mr-2" /> Refuser
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className={tc.buttonOutline}>Annuler</Button>
            <Button onClick={handleAccept} className={tc.button} data-testid="accept-order-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> Accepter
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Order Detail Modal
const OrderDetailModal = ({ order, open, onClose, drivers, vehicles, onCancel, onUpdateDriver, onUpdateVehicle, isDark }) => {
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const tc = useThemeClasses(isDark);

  useEffect(() => {
    if (order) {
      setSelectedDriverId(order.assigned_driver_id || null);
      setSelectedVehicleId(order.assigned_vehicle_id || null);
    }
  }, [order]);

  if (!order) return null;

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString("fr-FR") : "-";

  const handleAssign = () => {
    if (selectedDriverId && selectedVehicleId) {
      const driver = drivers.find(d => d.id === selectedDriverId);
      const vehicle = vehicles.find(v => v.id === selectedVehicleId);
      if (driver && vehicle) {
        onUpdateDriver(order.order_id, { first_name: driver.first_name, last_name: driver.last_name, phone: driver.phone, carrier_company_name: driver.carrier_company_name });
        onUpdateVehicle(order.order_id, { car_model: vehicle.car_model, plate_number: vehicle.plate_number, color: vehicle.color });
        setShowAssign(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-4xl max-h-[90vh] ${tc.card} ${tc.text} overflow-hidden`}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-xl">Commande #{order.order_id}</DialogTitle>
            <StatusBadge status={order.status} internalStatus={order.internal_status} isDark={isDark} />
          </div>
          <DialogDescription className={tc.textMuted}>Créée le {formatDate(order.created_at)}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className={`${isDark ? 'bg-slate-700/50' : 'bg-emerald-100'} mb-4`}>
              <TabsTrigger value="details" className="data-[state=active]:bg-emerald-500/20">Détails</TabsTrigger>
              <TabsTrigger value="passenger" className="data-[state=active]:bg-emerald-500/20">Passager</TabsTrigger>
              <TabsTrigger value="route" className="data-[state=active]:bg-emerald-500/20">Itinéraire</TabsTrigger>
              <TabsTrigger value="driver" className="data-[state=active]:bg-emerald-500/20">Chauffeur</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent} flex items-center gap-2`}><Clock className="w-4 h-4" /> Horaires</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Départ:</span> {formatDate(order.start_time)}</p>
                  <p className="text-sm"><span className={tc.textMuted}>Durée:</span> {order.estimated_duration_minutes} min</p>
                </CardContent></Card>
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent}`}>Prix</h4>
                  <p className="text-2xl font-bold">{order.price?.amount} {order.price?.currency}</p>
                  {order.driver_amount && <p className="text-sm text-emerald-500"><span className={tc.textMuted}>Chauffeur:</span> {order.driver_amount} {order.price?.currency}</p>}
                </CardContent></Card>
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent} flex items-center gap-2`}><Users className="w-4 h-4" /> Passagers</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Passagers:</span> {order.passengers}</p>
                  <p className="text-sm"><span className={tc.textMuted}>Bagages:</span> {order.luggage_places}</p>
                </CardContent></Card>
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent} flex items-center gap-2`}><Plane className="w-4 h-4" /> Vol</h4>
                  <p className="text-sm"><span className={tc.textMuted}>N° Vol:</span> {order.flight_number}</p>
                  <p className="text-sm"><span className={tc.textMuted}>Pancarte:</span> {order.shield_text}</p>
                </CardContent></Card>
              </div>
            </TabsContent>

            <TabsContent value="passenger">
              <Card className={tc.cardInner}><CardContent className="p-4 space-y-3">
                <h4 className={`font-semibold ${tc.textAccent} flex items-center gap-2`}><Users className="w-4 h-4" /> Passager principal</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><span className={tc.textMuted}>Prénom:</span> {order.main_passenger?.first_name}</p>
                  <p><span className={tc.textMuted}>Nom:</span> {order.main_passenger?.last_name}</p>
                  <p className="flex items-center gap-2"><Phone className={`w-4 h-4 ${tc.textMuted}`} />{order.main_passenger?.phone}</p>
                  <p className="flex items-center gap-2"><Mail className={`w-4 h-4 ${tc.textMuted}`} />{order.main_passenger?.email}</p>
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="route" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className="font-semibold text-emerald-500 flex items-center gap-2"><MapPin className="w-4 h-4" /> Départ</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Type:</span> {order.start_point?.type}</p>
                  {order.start_point?.iata && <p className="text-sm"><span className={tc.textMuted}>IATA:</span> {order.start_point.iata}</p>}
                  {order.start_point?.address && <p className="text-sm"><span className={tc.textMuted}>Adresse:</span> {order.start_point.address}</p>}
                </CardContent></Card>
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className="font-semibold text-rose-500 flex items-center gap-2"><MapPin className="w-4 h-4" /> Arrivée</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Type:</span> {order.end_point?.type}</p>
                  {order.end_point?.iata && <p className="text-sm"><span className={tc.textMuted}>IATA:</span> {order.end_point.iata}</p>}
                  {order.end_point?.address && <p className="text-sm"><span className={tc.textMuted}>Adresse:</span> {order.end_point.address}</p>}
                </CardContent></Card>
              </div>
            </TabsContent>

            <TabsContent value="driver" className="space-y-4">
              {order.driver_info ? (
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent}`}>Chauffeur assigné</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Nom:</span> {order.driver_info.first_name} {order.driver_info.last_name}</p>
                  <p className="text-sm"><span className={tc.textMuted}>Téléphone:</span> {order.driver_info.phone}</p>
                </CardContent></Card>
              ) : <Card className={tc.cardInner}><CardContent className="p-4 text-center"><p className={tc.textMuted}>Aucun chauffeur assigné</p></CardContent></Card>}

              {order.car_info && (
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-2">
                  <h4 className={`font-semibold ${tc.textAccent} flex items-center gap-2`}><Car className="w-4 h-4" /> Véhicule</h4>
                  <p className="text-sm"><span className={tc.textMuted}>Modèle:</span> {order.car_info.car_model}</p>
                  <p className="text-sm"><span className={tc.textMuted}>Plaque:</span> {order.car_info.plate_number}</p>
                </CardContent></Card>
              )}

              {(!order.driver_info || !order.car_info) && !showAssign && (
                <Button onClick={() => setShowAssign(true)} variant="outline" className={`${tc.buttonOutline} w-full border-emerald-500/50 text-emerald-500`}>
                  <Edit className="w-4 h-4 mr-2" /> Modifier l'assignation
                </Button>
              )}
              {showAssign && (
                <Card className={tc.cardInner}><CardContent className="p-4 space-y-4">
                  <LinkedSelect drivers={drivers} vehicles={vehicles} selectedDriverId={selectedDriverId} selectedVehicleId={selectedVehicleId} onDriverChange={setSelectedDriverId} onVehicleChange={setSelectedVehicleId} isDark={isDark} />
                  <div className="flex gap-2">
                    <Button onClick={handleAssign} className={tc.button}>Enregistrer</Button>
                    <Button variant="ghost" onClick={() => setShowAssign(false)}>Annuler</Button>
                  </div>
                </CardContent></Card>
              )}
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter className={`flex justify-between border-t ${isDark ? 'border-slate-700' : 'border-emerald-200'} pt-4`}>
          <div>
            {order.status !== "cancelled" && order.internal_status !== "rejected" && (
              <Button variant="destructive" onClick={() => onCancel(order.order_id)} data-testid="cancel-order-btn">
                <XCircle className="w-4 h-4 mr-2" /> Annuler
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose} className={tc.buttonOutline}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Notification Settings Modal
const NotificationSettingsModal = ({ open, onClose, api, isDark }) => {
  const [settings, setSettings] = useState({ email_recipients: [], sms_recipients: [], notifications_enabled: true, email_enabled: true, sms_enabled: true });
  const [newEmail, setNewEmail] = useState("");
  const [newSms, setNewSms] = useState("");
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const tc = useThemeClasses(isDark);

  useEffect(() => { if (open) loadSettings(); }, [open]);

  const loadSettings = async () => {
    try { const res = await api.get("/notifications/settings"); setSettings(res.data); } catch { toast.error("Erreur de chargement"); }
  };

  const saveSettings = async () => {
    setLoading(true);
    try { await api.put("/notifications/settings", settings); toast.success("Paramètres enregistrés"); } catch { toast.error("Erreur de sauvegarde"); } finally { setLoading(false); }
  };

  const addEmail = () => { if (newEmail && !settings.email_recipients.includes(newEmail)) { setSettings({ ...settings, email_recipients: [...settings.email_recipients, newEmail] }); setNewEmail(""); } };
  const removeEmail = (email) => setSettings({ ...settings, email_recipients: settings.email_recipients.filter(e => e !== email) });
  const addSms = () => { if (newSms && !settings.sms_recipients.includes(newSms)) { setSettings({ ...settings, sms_recipients: [...settings.sms_recipients, newSms] }); setNewSms(""); } };
  const removeSms = (phone) => setSettings({ ...settings, sms_recipients: settings.sms_recipients.filter(p => p !== phone) });

  const testNotification = async () => {
    setTesting(true);
    try { await api.post("/notifications/test"); toast.success("Notification test envoyée"); } catch { toast.error("Erreur d'envoi"); } finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl ${tc.card} ${tc.text}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-emerald-500" /> Paramètres des Notifications</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className={`flex items-center justify-between p-4 ${tc.cardInner} rounded-lg`}>
            <div><p className="font-medium">Notifications activées</p><p className={`text-sm ${tc.textMuted}`}>Activer/désactiver toutes les notifications</p></div>
            <Switch checked={settings.notifications_enabled} onCheckedChange={(v) => setSettings({...settings, notifications_enabled: v})} />
          </div>

          <Card className={tc.cardInner}><CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-emerald-500" /> Destinataires Email</h4>
              <Switch checked={settings.email_enabled} onCheckedChange={(v) => setSettings({...settings, email_enabled: v})} />
            </div>
            <div className="flex gap-2">
              <Input placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={tc.inputAlt} />
              <Button onClick={addEmail} size="sm" className={tc.button}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.email_recipients.map(email => (
                <Badge key={email} className={`${isDark ? 'bg-slate-600' : 'bg-emerald-100'} ${tc.text} flex items-center gap-1 pr-1`}>
                  {email}<button onClick={() => removeEmail(email)} className="ml-1 hover:text-rose-500"><XCircle className="w-3 h-3" /></button>
                </Badge>
              ))}
              {settings.email_recipients.length === 0 && <p className={`${tc.textMuted} text-sm`}>Aucun destinataire</p>}
            </div>
          </CardContent></Card>

          <Card className={tc.cardInner}><CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-500" /> Destinataires SMS</h4>
              <Switch checked={settings.sms_enabled} onCheckedChange={(v) => setSettings({...settings, sms_enabled: v})} />
            </div>
            <div className="flex gap-2">
              <Input placeholder="+33612345678" value={newSms} onChange={(e) => setNewSms(e.target.value)} className={tc.inputAlt} />
              <Button onClick={addSms} size="sm" className={tc.button}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.sms_recipients.map(phone => (
                <Badge key={phone} className={`${isDark ? 'bg-slate-600' : 'bg-emerald-100'} ${tc.text} flex items-center gap-1 pr-1`}>
                  {phone}<button onClick={() => removeSms(phone)} className="ml-1 hover:text-rose-500"><XCircle className="w-3 h-3" /></button>
                </Badge>
              ))}
              {settings.sms_recipients.length === 0 && <p className={`${tc.textMuted} text-sm`}>Aucun destinataire</p>}
            </div>
          </CardContent></Card>
        </div>

        <DialogFooter className={`flex justify-between border-t ${isDark ? 'border-slate-700' : 'border-emerald-200'} pt-4`}>
          <Button variant="outline" onClick={testNotification} disabled={testing} className={tc.buttonOutline}><Send className="w-4 h-4 mr-2" /> {testing ? "Envoi..." : "Tester"}</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className={tc.buttonOutline}>Annuler</Button>
            <Button onClick={saveSettings} disabled={loading} className={tc.button}>{loading ? "Sauvegarde..." : "Enregistrer"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Drivers Modal
const DriversModal = ({ open, onClose, drivers, vehicles, onCreateDriver, onUpdateDriver, onDeleteDriver, isDark }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", phone: "", email: "", carrier_company_name: "", default_vehicle_id: null });
  const tc = useThemeClasses(isDark);

  const resetForm = () => { setFormData({ first_name: "", last_name: "", phone: "", email: "", carrier_company_name: "", default_vehicle_id: null }); setEditingDriver(null); setShowForm(false); };
  const handleEdit = (driver) => { setEditingDriver(driver); setFormData({ first_name: driver.first_name, last_name: driver.last_name, phone: driver.phone, email: driver.email || "", carrier_company_name: driver.carrier_company_name || "", default_vehicle_id: driver.default_vehicle_id || null }); setShowForm(true); };
  const handleSubmit = async () => { if (editingDriver) await onUpdateDriver(editingDriver.id, formData); else await onCreateDriver(formData); resetForm(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-3xl ${tc.card} ${tc.text}`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-emerald-500" /> Gestion des Chauffeurs</DialogTitle><DialogDescription className={tc.textMuted}>Créer, modifier ou supprimer des chauffeurs</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {!showForm ? (
            <>
              <Button onClick={() => setShowForm(true)} className={tc.button}><Plus className="w-4 h-4 mr-2" /> Nouveau chauffeur</Button>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader><TableRow className={tc.tableHeader}><TableHead className={tc.textMuted}>Nom</TableHead><TableHead className={tc.textMuted}>Téléphone</TableHead><TableHead className={tc.textMuted}>Véhicule par défaut</TableHead><TableHead className={tc.textMuted}>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {drivers.map(driver => (
                      <TableRow key={driver.id} className={tc.tableRow}>
                        <TableCell className={tc.text}>{driver.first_name} {driver.last_name}</TableCell>
                        <TableCell className={tc.textMuted}>{driver.phone}</TableCell>
                        <TableCell className={tc.textMuted}>{driver.default_vehicle ? <span className="flex items-center gap-1"><Link2 className="w-3 h-3 text-emerald-500" />{driver.default_vehicle.car_model}</span> : "-"}</TableCell>
                        <TableCell><div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => handleEdit(driver)}><Edit className="w-4 h-4" /></Button><Button size="sm" variant="ghost" className="text-rose-500" onClick={() => onDeleteDriver(driver.id)}><Trash2 className="w-4 h-4" /></Button></div></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </>
          ) : (
            <Card className={tc.cardInner}><CardContent className="p-4 space-y-4">
              <h4 className={`${tc.textAccent} font-medium`}>{editingDriver ? "Modifier" : "Nouveau"} chauffeur</h4>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Prénom *" value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Nom *" value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Téléphone *" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Société" value={formData.carrier_company_name} onChange={(e) => setFormData({...formData, carrier_company_name: e.target.value})} className={tc.inputAlt} />
                <Select value={formData.default_vehicle_id || "none"} onValueChange={(v) => setFormData({...formData, default_vehicle_id: v === "none" ? null : v})}>
                  <SelectTrigger className={tc.inputAlt}><SelectValue placeholder="Véhicule par défaut" /></SelectTrigger>
                  <SelectContent className={tc.selectContent}><SelectItem value="none" className={tc.text}>Aucun</SelectItem>{vehicles.map(v => <SelectItem key={v.id} value={v.id} className={tc.text}>{v.car_model} - {v.plate_number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2"><Button onClick={handleSubmit} className={tc.button}>{editingDriver ? "Modifier" : "Créer"}</Button><Button variant="ghost" onClick={resetForm}>Annuler</Button></div>
            </CardContent></Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Vehicles Modal
const VehiclesModal = ({ open, onClose, vehicles, drivers, onCreateVehicle, onUpdateVehicle, onDeleteVehicle, isDark }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState({ car_model: "", plate_number: "", color: "", seats: 4, luggage_capacity: 3, category: "" });
  const tc = useThemeClasses(isDark);

  const resetForm = () => { setFormData({ car_model: "", plate_number: "", color: "", seats: 4, luggage_capacity: 3, category: "" }); setEditingVehicle(null); setShowForm(false); };
  const handleEdit = (vehicle) => { setEditingVehicle(vehicle); setFormData({ car_model: vehicle.car_model, plate_number: vehicle.plate_number, color: vehicle.color || "", seats: vehicle.seats, luggage_capacity: vehicle.luggage_capacity, category: vehicle.category || "" }); setShowForm(true); };
  const handleSubmit = async () => { if (editingVehicle) await onUpdateVehicle(editingVehicle.id, formData); else await onCreateVehicle(formData); resetForm(); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-3xl ${tc.card} ${tc.text}`}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Car className="w-5 h-5 text-emerald-500" /> Gestion des Véhicules</DialogTitle><DialogDescription className={tc.textMuted}>Créer, modifier ou supprimer des véhicules</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {!showForm ? (
            <>
              <Button onClick={() => setShowForm(true)} className={tc.button}><Plus className="w-4 h-4 mr-2" /> Nouveau véhicule</Button>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader><TableRow className={tc.tableHeader}><TableHead className={tc.textMuted}>Modèle</TableHead><TableHead className={tc.textMuted}>Plaque</TableHead><TableHead className={tc.textMuted}>Places</TableHead><TableHead className={tc.textMuted}>Chauffeur</TableHead><TableHead className={tc.textMuted}>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {vehicles.map(vehicle => (
                      <TableRow key={vehicle.id} className={tc.tableRow}>
                        <TableCell className={tc.text}>{vehicle.car_model}</TableCell>
                        <TableCell className={tc.textMuted}>{vehicle.plate_number}</TableCell>
                        <TableCell className={tc.textMuted}>{vehicle.seats}</TableCell>
                        <TableCell className={tc.textMuted}>{vehicle.default_driver ? <span className="flex items-center gap-1"><Link2 className="w-3 h-3 text-emerald-500" />{vehicle.default_driver.first_name}</span> : "-"}</TableCell>
                        <TableCell><div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => handleEdit(vehicle)}><Edit className="w-4 h-4" /></Button><Button size="sm" variant="ghost" className="text-rose-500" onClick={() => onDeleteVehicle(vehicle.id)}><Trash2 className="w-4 h-4" /></Button></div></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </>
          ) : (
            <Card className={tc.cardInner}><CardContent className="p-4 space-y-4">
              <h4 className={`${tc.textAccent} font-medium`}>{editingVehicle ? "Modifier" : "Nouveau"} véhicule</h4>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Modèle *" value={formData.car_model} onChange={(e) => setFormData({...formData, car_model: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Plaque *" value={formData.plate_number} onChange={(e) => setFormData({...formData, plate_number: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Couleur" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} className={tc.inputAlt} />
                <Input placeholder="Places" type="number" value={formData.seats} onChange={(e) => setFormData({...formData, seats: parseInt(e.target.value) || 4})} className={tc.inputAlt} />
              </div>
              <div className="flex gap-2"><Button onClick={handleSubmit} className={tc.button}>{editingVehicle ? "Modifier" : "Créer"}</Button><Button variant="ghost" onClick={resetForm}>Annuler</Button></div>
            </CardContent></Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Dashboard
const DashboardPage = ({ token, onLogout, isDark, toggleTheme }) => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAcceptReject, setShowAcceptReject] = useState(false);
  const [showDrivers, setShowDrivers] = useState(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const tc = useThemeClasses(isDark);

  const api = useMemo(() => createApiClient(token), [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, driversRes, vehiclesRes] = await Promise.all([api.get("/stats"), api.get("/orders", { params: { search: searchTerm, status: statusFilter || undefined } }), api.get("/drivers"), api.get("/vehicles")]);
      setStats(statsRes.data); setOrders(ordersRes.data); setDrivers(driversRes.data); setVehicles(vehiclesRes.data);
    } catch (error) { if (error.response?.status === HTTP_UNAUTHORIZED) onLogout(); else toast.error("Erreur de chargement"); } finally { setLoading(false); }
  }, [searchTerm, statusFilter, api, onLogout]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleViewOrder = async (orderId) => { try { const response = await api.get(`/orders/${orderId}`); setSelectedOrder(response.data); if (response.data.internal_status === "pending") { setPendingOrder(response.data); setShowAcceptReject(true); } else { setShowDetail(true); } } catch { toast.error("Erreur de chargement"); } };
  const handleAcceptOrder = async (orderId, acceptData) => { try { await api.post(`/orders/${orderId}/accept`, acceptData); toast.success("Course acceptée"); loadData(); } catch { toast.error("Erreur lors de l'acceptation"); } };
  const handleRejectOrder = async (orderId, reason = "") => { try { await api.post(`/orders/${orderId}/reject`, { reason }); toast.success("Course refusée"); loadData(); } catch { toast.error("Erreur lors du refus"); } };
  const handleCancelOrder = async (orderId) => { if (!window.confirm("Annuler cette commande ?")) return; try { const res = await api.post(`/orders/${orderId}/cancel`); toast.success(`Commande annulée. Pénalité: ${res.data.penalty.amount} ${res.data.penalty.currency}`); loadData(); setShowDetail(false); } catch { toast.error("Erreur d'annulation"); } };
  const handleUpdateDriver = async (orderId, driverData) => { try { await api.put(`/orders/${orderId}/driver`, driverData); toast.success("Chauffeur mis à jour"); handleViewOrder(orderId); } catch { toast.error("Erreur de mise à jour"); } };
  const handleUpdateVehicle = async (orderId, vehicleData) => { try { await api.put(`/orders/${orderId}/car`, vehicleData); toast.success("Véhicule mis à jour"); } catch { toast.error("Erreur de mise à jour"); } };
  const handleCreateDriver = async (data) => { try { const res = await api.post("/drivers", data); toast.success("Chauffeur créé"); loadData(); return res.data; } catch { toast.error("Erreur de création"); return null; } };
  const handleUpdateDriverRecord = async (id, data) => { try { await api.put(`/drivers/${id}`, data); toast.success("Chauffeur modifié"); loadData(); } catch { toast.error("Erreur de modification"); } };
  const handleDeleteDriver = async (id) => { if (!window.confirm("Supprimer ce chauffeur ?")) return; try { await api.delete(`/drivers/${id}`); toast.success("Chauffeur supprimé"); loadData(); } catch { toast.error("Erreur de suppression"); } };
  const handleCreateVehicle = async (data) => { try { const res = await api.post("/vehicles", data); toast.success("Véhicule créé"); loadData(); return res.data; } catch { toast.error("Erreur de création"); return null; } };
  const handleUpdateVehicleRecord = async (id, data) => { try { await api.put(`/vehicles/${id}`, data); toast.success("Véhicule modifié"); loadData(); } catch { toast.error("Erreur de modification"); } };
  const handleDeleteVehicle = async (id) => { if (!window.confirm("Supprimer ce véhicule ?")) return; try { await api.delete(`/vehicles/${id}`); toast.success("Véhicule supprimé"); loadData(); } catch { toast.error("Erreur de suppression"); } };

  return (
    <div className={`min-h-screen ${tc.bg}`} data-testid="dashboard">
      <header className={`${tc.header} backdrop-blur-md border-b sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-lg">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Travel CAB</h1>
              <p className={`text-xs ${tc.textMuted}`}>Backoffice</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={toggleTheme} className={tc.textMuted} data-testid="theme-toggle">
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="outline" onClick={() => setShowNotifications(true)} className={tc.buttonOutline} data-testid="notifications-btn"><Bell className="w-4 h-4" /></Button>
            <Button variant="outline" onClick={() => setShowDrivers(true)} className={tc.buttonOutline} data-testid="manage-drivers-btn"><UserCheck className="w-4 h-4 mr-2" /> Chauffeurs</Button>
            <Button variant="outline" onClick={() => setShowVehicles(true)} className={tc.buttonOutline} data-testid="manage-vehicles-btn"><Car className="w-4 h-4 mr-2" /> Véhicules</Button>
            <Button variant="ghost" onClick={onLogout} className={tc.textMuted} data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard title="Total" value={stats.total_orders} icon={Package} colorClass={tc.statCards.primary} isDark={isDark} />
            <StatCard title="En attente" value={stats.pending_orders} icon={AlertCircle} colorClass={tc.statCards.purple} isDark={isDark} />
            <StatCard title="Acceptées" value={stats.active_orders} icon={CheckCircle} colorClass={tc.statCards.green} isDark={isDark} />
            <StatCard title="Annulées" value={stats.cancelled_orders} icon={XCircle} colorClass={tc.statCards.red} isDark={isDark} />
            <StatCard title="Revenu" value={`${stats.total_revenue.toFixed(0)}€`} icon={TrendingUp} colorClass={tc.statCards.blue} isDark={isDark} />
          </div>
        )}

        <Card className={`${tc.card} backdrop-blur-sm`}>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className={`${tc.text} flex items-center gap-2`}><Package className="w-5 h-5 text-emerald-500" /> Commandes</CardTitle>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${tc.textMuted}`} />
                  <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`pl-10 w-48 ${tc.input}`} data-testid="search-input" />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${tc.select} border rounded-md px-3 text-sm`} data-testid="status-filter">
                  <option value="">Tous</option><option value="completed">Actifs</option><option value="cancelled">Annulés</option>
                </select>
                <Button variant="outline" onClick={loadData} className={tc.buttonOutline} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>
            ) : orders.length === 0 ? (
              <div className={`text-center py-12 ${tc.textMuted}`}><Package className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune commande</p></div>
            ) : (
              <Table>
                <TableHeader><TableRow className={`${tc.tableHeader} hover:bg-transparent`}><TableHead className={tc.textMuted}>ID</TableHead><TableHead className={tc.textMuted}>Statut</TableHead><TableHead className={tc.textMuted}>Passager</TableHead><TableHead className={tc.textMuted}>Date</TableHead><TableHead className={tc.textMuted}>Prix</TableHead><TableHead className={tc.textMuted}>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {orders.map(order => (
                    <TableRow key={order.order_id} className={`${tc.tableRow} cursor-pointer`} onClick={() => handleViewOrder(order.order_id)} data-testid={`order-row-${order.order_id}`}>
                      <TableCell className="font-mono text-emerald-500">{order.order_id}</TableCell>
                      <TableCell><StatusBadge status={order.status} internalStatus={order.internal_status} isDark={isDark} /></TableCell>
                      <TableCell className={tc.text}>{order.passenger_name}</TableCell>
                      <TableCell className={tc.textMuted}>{order.start_time ? new Date(order.start_time).toLocaleDateString("fr-FR") : "-"}</TableCell>
                      <TableCell className={`${tc.text} font-medium`}>{order.price_amount} {order.price_currency}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewOrder(order.order_id); }} className="text-emerald-500 hover:text-emerald-400" data-testid={`view-order-${order.order_id}`}><Eye className="w-4 h-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <AcceptRejectModal order={pendingOrder} open={showAcceptReject} onClose={() => { setShowAcceptReject(false); setPendingOrder(null); }} drivers={drivers} vehicles={vehicles} onAccept={handleAcceptOrder} onReject={handleRejectOrder} onCreateDriver={handleCreateDriver} onCreateVehicle={handleCreateVehicle} isDark={isDark} />
      <OrderDetailModal order={selectedOrder} open={showDetail} onClose={() => setShowDetail(false)} drivers={drivers} vehicles={vehicles} onCancel={handleCancelOrder} onUpdateDriver={handleUpdateDriver} onUpdateVehicle={handleUpdateVehicle} isDark={isDark} />
      <NotificationSettingsModal open={showNotifications} onClose={() => setShowNotifications(false)} api={api} isDark={isDark} />
      <DriversModal open={showDrivers} onClose={() => setShowDrivers(false)} drivers={drivers} vehicles={vehicles} onCreateDriver={handleCreateDriver} onUpdateDriver={handleUpdateDriverRecord} onDeleteDriver={handleDeleteDriver} isDark={isDark} />
      <VehiclesModal open={showVehicles} onClose={() => setShowVehicles(false)} vehicles={vehicles} drivers={drivers} onCreateVehicle={handleCreateVehicle} onUpdateVehicle={handleUpdateVehicleRecord} onDeleteVehicle={handleDeleteVehicle} isDark={isDark} />
    </div>
  );
};

// App
function App() {
  const { token, login, logout, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="App">
      <Toaster position="top-right" theme={isDark ? "dark" : "light"} />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLogin={login} isDark={isDark} toggleTheme={toggleTheme} />} />
          <Route path="/" element={isAuthenticated ? <DashboardPage token={token} onLogout={logout} isDark={isDark} toggleTheme={toggleTheme} /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
